// src/ryan/config/ryanMercadopago.ts
//
// Instância do SDK Mercado Pago EXCLUSIVA do RYAN — usa a Public Key da
// aplicação própria do RYAN (REACT_APP_RYAN_MERCADOPAGO_PUBLIC_KEY), nunca a
// do ENEM (config/mercadopago.ts, arquivo separado). O script do SDK em si
// (sdk.mercadopago.com/js/v2, já carregado globalmente em public/index.html)
// é só a biblioteca — não carrega credencial nenhuma, cada plataforma
// instancia com sua própria chave.

declare const process: { env: Record<string, string | undefined> };

// SEM fallback fixo pra uma chave de TESTE — foi exatamente esse fallback
// (`TEST-ca8b0608-...`) que causou um incidente real em produção: quando o
// build de produção não foi refeito depois de configurar
// REACT_APP_RYAN_MERCADOPAGO_PUBLIC_KEY, o bundle ficava silenciosamente
// preso na chave de teste enquanto o backend (RYAN_MERCADOPAGO_ACCESS_TOKEN)
// já usava credenciais reais — o Brick tokenizava o cartão em modo TESTE, o
// backend tentava cobrar esse token com um Access Token de PRODUÇÃO, e o
// Mercado Pago rejeitava a cobrança antes de qualquer tentativa real no
// emissor do cartão (nenhuma cobrança aparecia no extrato). Sem fallback, se
// a env var faltar de novo, a falha é imediata e ruidosa (initRyanMercadoPago
// loga erro e retorna null) em vez de silenciosamente processar em modo
// teste com um backend de produção.
export const RYAN_MERCADOPAGO_PUBLIC_KEY = process.env.REACT_APP_RYAN_MERCADOPAGO_PUBLIC_KEY;

let mpInstance: any = null;

// Diagnóstico de qual build do SDK está no ar. O script em si é "evergreen"
// (sdk.mercadopago.com/js/v2, sem número de versão) e não existe forma
// documentada nem funcional de fixar uma versão — confirmado tanto na
// documentação oficial (não menciona pin/versionamento) quanto na prática
// (sdk.mercadopago.com/js/v1, v2.0, v2.1 e v3 retornam 404; só /js/v2
// existe). O bundle também não expõe nenhuma propriedade de versão própria
// em window.MercadoPago (as únicas strings de versão embutidas no bundle
// são de dependências internas — ex: um parser de user-agent e um polyfill
// — não do SDK do Mercado Pago em si). O header `Last-Modified` da resposta
// é o único sinal disponível de qual build está no ar; logado aqui (fetch
// HEAD best-effort, não bloqueia o init) pra, se um bug reaparecer, dar pra
// cruzar a data com mudanças/incidentes do lado do Mercado Pago
// (status.mercadopago.com).
const logSdkBuildInfo = () => {
    fetch('https://sdk.mercadopago.com/js/v2', { method: 'HEAD', cache: 'no-store' })
        .then((res) => {
            console.log('[Ryan][MP SDK] build info no momento da inicialização:', {
                lastModified: res.headers.get('last-modified'),
                cacheControl: res.headers.get('cache-control'),
            });
        })
        .catch(() => { /* diagnóstico best-effort — falha aqui não deve afetar o checkout */ });
};

export const initRyanMercadoPago = () => {
    if (mpInstance) return mpInstance;
    if (typeof window === 'undefined') return null;

    if (!RYAN_MERCADOPAGO_PUBLIC_KEY) {
        console.error('❌ [Ryan] REACT_APP_RYAN_MERCADOPAGO_PUBLIC_KEY não definida neste build — nenhuma chave (nem de teste) foi usada. Confira a env var no ambiente de build do frontend e refaça o deploy.');
        return null;
    }

    const MercadoPagoClass = (window as any).MercadoPago;
    if (!MercadoPagoClass) {
        console.error('❌ [Ryan] SDK do MercadoPago não carregado');
        return null;
    }

    try {
        mpInstance = new MercadoPagoClass(RYAN_MERCADOPAGO_PUBLIC_KEY, { locale: 'pt-BR' });
        // Loga se a chave carregada é de TESTE — sinal de alerta imediato caso o
        // backend já esteja em produção (incompatibilidade de ambiente, causa
        // raiz do incidente documentado acima).
        if (RYAN_MERCADOPAGO_PUBLIC_KEY.startsWith('TEST-')) {
            console.warn('⚠️ [Ryan] Public Key de TESTE carregada neste build. Se o backend (RYAN_MERCADOPAGO_ACCESS_TOKEN) já estiver em produção, todo pagamento com cartão real vai falhar.');
        }
        logSdkBuildInfo();
        return mpInstance;
    } catch (error) {
        console.error('❌ [Ryan] Erro ao inicializar Mercado Pago:', error);
        return null;
    }
};

export const resetRyanMercadoPago = () => {
    mpInstance = null;
};
